import type { Stage } from '@crm/shared';
import { useEffect, useState } from 'react';

import { KanbanBoard } from '../components/kanban/KanbanBoard';
import { PipelineList } from '../components/pipeline/PipelineList';
import { PipelineTable } from '../components/pipeline/PipelineTable';
import { apiClient, type Custodian, type Opportunity } from '../lib/api';

type PipelineView = 'kanban' | 'table' | 'list';

const VIEW_KEY = 'crm.pipeline.view';

function readView(): PipelineView {
  if (typeof window === 'undefined') return 'kanban';
  const raw = window.localStorage.getItem(VIEW_KEY);
  if (raw === 'kanban' || raw === 'table' || raw === 'list') return raw;
  return 'kanban';
}

export function PipelinePage() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [view, setView] = useState<PipelineView>(() => readView());
  const [custodians, setCustodians] = useState<Custodian[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rows, banks] = await Promise.all([
          apiClient.listOpportunities(),
          apiClient.listCustodians().catch(() => [] as Custodian[]),
        ]);
        if (!cancelled) {
          setOpportunities(rows);
          setCustodians(banks);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function handlePatched(updated: Opportunity) {
    setOpportunities((curr) => curr.map((o) => (o.id === updated.id ? updated : o)));
  }

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch {
      // localStorage may be disabled — ignore silently, fall back to default.
    }
  }, [view]);

  async function handleStageChange(opportunityId: string, toStage: Stage) {
    const previous = opportunities;
    setOpportunities((rows) =>
      rows.map((o) => (o.id === opportunityId ? { ...o, stage: toStage } : o)),
    );
    try {
      const updated = await apiClient.transitionStage(opportunityId, { to_stage: toStage });
      setOpportunities((rows) =>
        rows.map((o) =>
          o.id === opportunityId
            ? {
                ...o,
                stage: updated.stage,
                stage_probability_pct: updated.stage_probability_pct,
                stage_probability_override: updated.stage_probability_override,
                score_chf: updated.score_chf,
              }
            : o,
        ),
      );
      setToast(null);
    } catch (err) {
      setOpportunities(previous);
      const e = err as Error & { status?: number; payload?: { error?: string; message?: string } };
      if (e.status === 422 && e.payload?.error === 'kyc_not_approved') {
        setToast(
          e.payload.message ?? 'KYC not approved — submit KYC before onboarding (FINMA + SEC).',
        );
        window.setTimeout(() => setToast(null), 6000);
      } else {
        setToast(`Move failed — ${e.message}`);
        window.setTimeout(() => setToast(null), 4000);
      }
    }
  }

  return (
    <div className="flex flex-col min-h-screen">
      <header className="px-8 pt-10 pb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase">— 03 / Pipeline</p>
          <h1 className="mt-2 font-display text-4xl text-graphite font-semibold">
            Opportunities pipeline
          </h1>
          <p className="mt-2 text-ink-secondary text-sm">
            {opportunities.length} opportunities · real-time scoring · audit trail active
          </p>
        </div>

        <div
          role="tablist"
          aria-label="Pipeline view"
          className="inline-flex border border-platinum rounded-card overflow-hidden bg-surface"
        >
          {(
            [
              ['kanban', 'Kanban'],
              ['table', 'Tableau'],
              ['list', 'Liste'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={view === key}
              onClick={() => setView(key)}
              className={
                'px-4 py-2 text-xs uppercase tracking-wider transition-colors ' +
                (view === key
                  ? 'bg-graphite text-white'
                  : 'text-ink-secondary hover:text-ink hover:bg-glacier')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 min-h-[60vh] overflow-hidden">
        {error ? (
          <div className="p-12 text-semantic-error" role="alert">
            {error}
          </div>
        ) : view === 'kanban' ? (
          <KanbanBoard
            opportunities={opportunities}
            onStageChange={handleStageChange}
            loading={loading}
          />
        ) : view === 'table' ? (
          <PipelineTable
            opportunities={opportunities}
            custodians={custodians}
            onPatched={handlePatched}
          />
        ) : (
          <PipelineList opportunities={opportunities} />
        )}
      </div>

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-semantic-error text-white
                     px-5 py-3 rounded-card shadow-card text-sm font-medium max-w-[90vw] text-center"
        >
          {toast}
        </div>
      )}
    </div>
  );
}
